import { TableActionsWrapper, TableBadgeLabel } from '@components'

import { Flex, Icon, SearchInput, Space, Spacing, useModal } from '@billy/design-system'
import { ChangeEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Product } from '@features/products/types/product'
import { useVatRates } from '@features/vatRates/contexts/vatRatesContext'
import { HandleVatRateSelect, VatRateSelect } from '@features/vatRates/elements/VatRateSelect'
import { VatRateItemValue } from '@features/vatRates/elements/VatRateSelect/types/vatRateItemValue'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../enums/modalId'
import { useSegment } from '../../../../../../hooks'
import { DeleteProductsButton } from '../DeleteProductsButton'

export interface ProductsTableHeaderProps {
  onSearchChange: (query: string) => void
  onVatRateSelect: (vatRate?: VatRateItemValue) => void
  productCount: number
  searchQuery?: string
  selectedProducts: Product[]
  selectedVatRate?: VatRateItemValue
}

export const ProductsTableHeader = ({
  onSearchChange,
  onVatRateSelect,
  productCount,
  searchQuery,
  selectedProducts,
  selectedVatRate,
}: ProductsTableHeaderProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { track } = useSegment()
  const { isLoading: isLoadingVatRates, vatRates } = useVatRates()

  const { open: openDeleteProductsConfirmationModal } = useModal(ModalId.DeleteProductsConfirmationModal)
  const { open: openUpdateProductsVatConfirmationModal } = useModal(ModalId.UpdateProductsVatConfirmationModal)

  const shouldShowBulkActions = selectedProducts.length > 0
  const shouldShowVatRateSelect = !organization?.isVatExempted

  const handleVatRateSelect: HandleVatRateSelect = useCallback(
    (id, vatRate) => {
      track('XXX - Invoicing Core - Item lists - Bulk change VAT dropdown extended - Value clicked')
      onVatRateSelect(vatRate)
      openUpdateProductsVatConfirmationModal()
    },
    [onVatRateSelect, openUpdateProductsVatConfirmationModal, track],
  )

  const handleDeleteButtonClick = useCallback(() => {
    openDeleteProductsConfirmationModal()
  }, [openDeleteProductsConfirmationModal])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value),
    [onSearchChange],
  )

  const handleSearchClear = useCallback(() => onSearchChange(''), [onSearchChange])

  return (
    <>
      <TableActionsWrapper>
        <Flex flexDirection="row" alignItems="center" justifyContent="space-between" flexGrow={1}>
          <Flex flexDirection="row" alignItems="center" flexGrow={1}>
            <TableBadgeLabel amount={productCount} color="neutral">
              {t('products.table.header')}
            </TableBadgeLabel>
            <SearchInput
              focused={!!searchQuery}
              onChangeDebounced={handleSearchChange}
              onClear={handleSearchClear}
              placeholder={`${t('search')}...`}
              value={searchQuery}
            />
          </Flex>
          <Space size="s40" />
          {shouldShowBulkActions && (
            <Flex flexGrow={0} gap={Spacing.S8}>
              {shouldShowVatRateSelect && (
                <VatRateSelect
                  dropdownFetching={isLoadingVatRates}
                  dropdownSize="fitTrigger"
                  onSelect={handleVatRateSelect}
                  prefix={<Icon icon="percent" />}
                  placeholder={t('products.table.header.change_vat')}
                  selectedId={selectedVatRate?.id}
                  vatRates={vatRates}
                  withDetails
                  size="sm"
                />
              )}
              <DeleteProductsButton productsCount={selectedProducts.length} onClick={handleDeleteButtonClick} />
            </Flex>
          )}
        </Flex>
      </TableActionsWrapper>
    </>
  )
}
